import React from "react";

// Customizable Area Start
import {
  Container,
  Box,
  Typography,
  styled,
  TextField,
  InputAdornment,
  Avatar,
  Popover
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ChatDetails from "./ChatDetails.web";
import NavigationMenu from "../../navigationmenu/src/NavigationMenu.web";
import AppFooter from "../../../components/src/AppFooter.web";
import Loader from "../../../components/src/Loader.web";
import { Conversation } from "./ChatController";
import ChatSummaryCard from "../../../components/src/ChatSummaryCard.web";
import { CustomContext } from "../../../components/src/CustomContext.web";
import FullPageLoader from "../../../components/src/FullPageLoader.web";
import GoogleAd from "../../../components/src/GoogleAd.web";
// Customizable Area End

import ChatController, { configJSON, Props } from "./ChatController";

export default class Chat extends ChatController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  // Customizable Area End

  render() {
    // Customizable Area Start
    const { navigation } = this.props;
    const {
      isLoading,
      conversationsList,
      searchValue,
      searchListAnchorEl,
      searchedChatList,
      isSearchingUser,
      isChatsLoading,
    } = this.state;
    const open = Boolean(searchListAnchorEl) && searchValue.length > 2;
    const searchPopupId = open ? "search-list-popover" : undefined;
    const unreadContext = this.context as React.ContextType<
      typeof CustomContext
    >;

    return (
      <>
        <NavigationMenu id="" navigation={navigation} />
        {isLoading ? (
          <Box height="60vh">
            <FullPageLoader isLoading />
          </Box>
        ) : (
          <OuterWrapper>
            <Container maxWidth="lg">
              <MainWrapper>
                <ChatListWrapper chatselected={unreadContext.selectedChatUID}>
                  <ChatHeadingWrapper>
                    <MainHeading data-test-id="chat-heading" variant="h4">
                      {configJSON.chatsText}
                    </MainHeading>
                  </ChatHeadingWrapper>
                  <SearchFieldWrapper>
                    <SearchField
                      autoComplete="off"
                      value={searchValue}
                      data-test-id="search-field"
                      aria-describedby={searchPopupId}
                      onClick={this.handleSearchPopupClick}
                      onChange={this.handleSearchValueChange}
                      placeholder={configJSON.searchText}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchRoundedIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Popover
                      open={open}
                      disableAutoFocus
                      id={searchPopupId}
                      data-test-id="search-popover"
                      anchorEl={searchListAnchorEl}
                      onClose={this.handleSearchPopupClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            width: searchListAnchorEl
                              ? searchListAnchorEl.offsetWidth
                              : "auto",
                            boxShadow: "none",
                            borderBottom: "1px solid #CBD5E1",
                            borderRadius: "0px",
                          },
                        },
                      }}
                    >
                      <SearchUserWrapper>
                        <Loader wrapperHeight="100px" loading={isSearchingUser}>
                          {searchedChatList.length > 0 ? (
                            searchedChatList.map((userInfo) => {
                              const {
                                id,
                                attributes: {
                                  full_name,
                                  user_image_url,
                                  roles,
                                },
                              } = userInfo;
                              return (
                                <SearchUsersContentWrapper
                                  key={`search-${id}`}
                                  data-test-id="searched-user"
                                  onClick={() =>
                                    this.handleNewConversation(userInfo.id)
                                  }
                                >
                                  <SearchUserAvatar
                                    src={user_image_url}
                                    alt={full_name}
                                  />
                                  <SearchUserContent>{`${full_name} (${roles})`}</SearchUserContent>
                                </SearchUsersContentWrapper>
                              );
                            })
                          ) : (
                            <SearchUsersContentWrapper data-test-id="searched-user">
                              <SearchUserContent
                                width="100%"
                                textAlign="center"
                              >
                                {configJSON.noUsersFoundText}
                              </SearchUserContent>
                            </SearchUsersContentWrapper>
                          )}
                        </Loader>
                      </SearchUserWrapper>
                    </Popover>
                  </SearchFieldWrapper>
                  <Loader
                    wrapperStyles={{ flex: 1 }}
                    loading={conversationsList.length === 0 && isChatsLoading}
                  >
                    <ChatListContentWrapper>
                      {conversationsList.length > 0 ? (
                        <ChatsList
                          className="custom_scroll"
                          ref={this.chatListRef}
                        >
                          {conversationsList.map((chatInfo: Conversation) => {
                            const { conversationId, conversationWith } =
                              chatInfo;
                            const isActiveChat =
                              unreadContext.selectedChatUID ===
                              conversationWith.uid;
                            return (
                              <ChatSummaryCard
                                key={conversationId}
                                data-test-id="chat-summary"
                                isActiveChat={isActiveChat}
                                chatInfo={chatInfo}
                                toggleSelection={this.toggleChatSelection}
                              />
                            );
                          })}
                          <Box mt={1}>
                            <Loader
                              size={20}
                              loading={
                                conversationsList.length > 0 && isChatsLoading
                              }
                            />
                          </Box>
                        </ChatsList>
                      ) : (
                        <InfoGrayMessage>
                          {configJSON.noChatsToShowMessage}
                        </InfoGrayMessage>
                      )}
                    </ChatListContentWrapper>
                  </Loader>
                </ChatListWrapper>
                <ChatDetailsWrapper
                  chatselected={unreadContext.selectedChatUID}
                >
                  <ChatDetails
                    data-test-id="chat-details"
                    navigation={navigation}
                    selectedChatUID={unreadContext.selectedChatUID}
                    toggleChatSelection={this.toggleChatSelection}
                    markReadMessagesCon={this.markReadMessagesCon}
                    handleRemoveConversation={this.handleRemoveConversation}
                    updateConversationByMessage={this.handleOutgoingMessage}
                  />
                </ChatDetailsWrapper>
              </MainWrapper>
              <GoogleAd responsive format="auto" sx={{ mt: 6 }} slot="4904211037" />
            </Container>
          </OuterWrapper>
        )}
        <AppFooter navigation={navigation} />
      </>
    );
    // Customizable Area End
  }
}

// Customizable Area Start
const OuterWrapper = styled(Box)({
  backgroundColor: "#EFF6FF",
  padding: "40px",
  "@media (max-width: 650px)": {
    padding: "25px",
  },
  "@media (max-width: 450px)": {
    padding: "10px",
  },
  "& .MuiContainer-root": {
    padding: "0px",
  },
});

const MainWrapper = styled(Box)({
  border: "1px solid #CBD5E1",
  borderRadius: "12px",
  display: "flex",
  overflow: "hidden",
  boxSizing: 'border-box',
  backgroundColor: "#fff",
  height: "80vh",
  "@media (max-width: 450px)": {
    height: "77vh",
  },
});

const ChatListWrapper = styled(Box)(
  ({ chatselected }: { chatselected: string }) => ({
    width: "33%",
    borderRight: "1px solid #CBD5E1",
    display: "flex",
    flexDirection: "column",
    "@media (max-width: 850px)": {
      width: "45%",
    },
    "@media (max-width: 650px)": {
      display: chatselected ? "none" : "flex",
      width: "100%",
    },
  })
);

const ChatDetailsWrapper = styled(Box)(
  ({ chatselected }: { chatselected: string }) => ({
    width: "67%",
    "@media (max-width: 850px)": {
      width: "55%",
    },
    "@media (max-width: 650px)": {
      width: "100%",
      display: chatselected ? "block" : "none",
    },
  })
);

const ChatHeadingWrapper = styled(Box)({
  padding: "6px",
  height: "56px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderBottom: "1px solid #CBD5E1",
});

const MainHeading = styled(Typography)({
  color: "#0D0C22",
  fontSize: "1.25rem",
  fontWeight: 600,
  letterSpacing: "-0.15px",
  lineHeight: "24.38px",
  textAlign: "center",
});

const SearchFieldWrapper = styled(Box)({
  borderBottom: "1px solid #CBD5E1",
  height: "56px",
});

const SearchField = styled(TextField)({
  width: "100%",
  "& fieldset": {
    border: "none",
  },
  "& input": {
    color: "#64748B",
    fontWeight: 500,
    letterSpacing: "-0.15px",
    lineHeight: "19.5px",
    fontSize: "1rem",
  },
  "& svg": {
    color: "#94A3B8",
    height: "23px",
    width: "23px",
  },
});

const SearchUserWrapper = styled(Box)({
  overflowY: "auto",
  maxHeight: "276px",
});

const SearchUsersContentWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "10px",
  cursor: "pointer",
});

const SearchUserAvatar = styled(Avatar)({
  height: "40px",
  width: "40px",
  objectPosition: "top",
});

const SearchUserContent = styled(Typography)({
  fontSize: "1rem",
  fontWeight: 500,
  color: "#1E293B",
});

const ChatListContentWrapper = styled(Box)({
  flex: 1,
  display: "flex",
  alignItems: "center",
  width: "100%",
  overflow: "hidden",
});

const InfoGrayMessage = styled(Typography)({
  color: "#94A3B8",
  fontSize: "1.5rem",
  fontWeight: 500,
  lineHeight: "29.26px",
  textAlign: "center",
  width: "100%",
  padding: "0 20px",
  "@media (max-width: 850px)": {
    fontSize: "1.2rem",
  },
  "@media (max-width: 414px)": {
    fontSize: "1rem",
  },
});

const ChatsList = styled(Box)({
  height: "100%",
  width: "100%",
  overflowY: "auto",
});
// Customizable Area End
